import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  CardHeader,
  Button,
  Spinner,
  Input,
  Badge,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import RolePermissionAssignModal from './RolePermissionAssignModal';

const RolePermissionManagement = () => {
  const token = getAuthToken();

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [assignModal, setAssignModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchRoles = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const queryParams = {
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      };

      const qs = new URLSearchParams(queryParams);

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Name', debouncedSearchText.trim());
      }

      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const rolesRes = await fetch(`${api.API_URL}/role?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!rolesRes.ok) {
        throw new Error(`HTTP error! status: ${rolesRes.status}`);
      }

      const rolesData = await rolesRes.json();
      setRoles(rolesData.data?.items || []);
      setTotalRows(rolesData.data?.totalItems || 0);
    } catch (error) {
      toast.error(`Error loading roles: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    token,
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const openAssignModal = role => {
    setSelectedRole(role);
    setAssignModal(true);
  };

  const columns = [
    {
      id: 'name',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Role Name
        </span>
      ),
      selector: row => row.name || row.id,
      sortable: true,
      cell: row => (
        <div className='d-flex align-items-center gap-2'>
          <span>{row.name || row.id}</span>
          {row.name === 'Super Admin' && (
            <Badge color='warning' className='fs-10 rounded-pill ms-1'>
              Bypass
            </Badge>
          )}
        </div>
      ),
    },
    {
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted text-center w-100'>
          Action
        </span>
      ),
      width: '200px',
      center: true,
      cell: row => (
        <div className='d-flex gap-2 justify-content-center'>
          <Button
            color='primary'
            size='sm'
            className='btn-label'
            onClick={() => openAssignModal(row)}
          >
            <i className='ri-shield-keyhole-line label-icon align-middle fs-16 me-1'></i>
            Manage Permissions
          </Button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <BreadCrumb title='View Data' pageTitle='Role Permission' />
      <Card className='border-0 shadow-sm mb-0'>
        <CardHeader className='border-0 pb-0'>
          <h5 className='card-title mb-0'>Role Permission</h5>
        </CardHeader>
        <CardBody>
          <Row className='mb-3'>
            <Col md={4}>
              <div className='search-box'>
                <Input
                  type='text'
                  className='form-control'
                  placeholder='Search role name...'
                  value={searchText}
                  onChange={handleSearch}
                />
                <i className='ri-search-line search-icon'></i>
              </div>
            </Col>
          </Row>
          <DataTable
            columns={columns}
            data={roles}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            onChangePage={page => setCurrentPage(page)}
            onChangeRowsPerPage={rows => {
              setRowsPerPage(rows);
              setCurrentPage(1);
            }}
            onSort={(column, dir) => {
              setSortField(column.id);
              setSortDirection(dir);
              setCurrentPage(1);
            }}
            sortServer
            highlightOnHover
            responsive
            striped
            progressPending={isLoading}
            progressComponent={<Spinner color='primary' className='my-4' />}
            noDataComponent={
              <p className='my-4 text-muted text-center'>Data Role kosong.</p>
            }
          />
        </CardBody>
      </Card>

      <RolePermissionAssignModal
        isOpen={assignModal}
        toggle={() => setAssignModal(false)}
        token={token}
        selectedRole={selectedRole}
      />

      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default RolePermissionManagement;
